import { Container, isMobile } from "pixi.js";
import { SpineLayout } from "@pixi/spine-layout";
import { HeroController } from "./HeroController";
import { ParadeController } from "./ParadeController";
import { config } from "../settings/config";
import type { HeroData } from "../helpers/types";

const POSITION_ANIMATION_COOLDOWN_MS = 2000;

type HeroesMap = Map<string, HeroController>;


export class HeroesController {
    private heroes: HeroesMap = new Map();
    private tracksContainers: Container[];
    private lastPositions = new Map<string, number>();
    private lastAnimationAt = new Map<string, number>();
    readonly parade: ParadeController;

    constructor(private layout: SpineLayout) {
        this.createHeroes();
        this.tracksContainers = this.createTracksContainers();
        this.parade = new ParadeController(layout, this.heroes);
    }

    private createHeroes() {
        this.heroes = new Map();

        this.layout.getSpines().forEach((spine, id) => {
            if (id.startsWith(config.spineHeroPointer)) {
                const hero = new HeroController(id, spine);

                this.heroes.set(id, hero);
            }
        });
    }

    private createTracksContainers(): Container[] {
        const raceTracks: Container[] = [];

        this.layout.getSpine('track')?.skeleton.data.slots.forEach((slot) => {
            if (slot.name.startsWith(`spine_${config.spineHeroPointer}`)) {
                const trackContainer = new Container();

                this.layout.addSlotChild('track', slot.name, trackContainer);

                raceTracks.push(trackContainer);
            }
        });

        return raceTracks;
    }

    private resetPositions() {
        this.heroes.forEach((hero) => hero.x = 0);
    }

    private clearTracks() {
        this.tracksContainers.forEach((trackContainer) => {
            trackContainer.removeChildren();
        });
    }

    setAllIdle() {
        this.heroes.forEach((hero) => {
            hero.state = 'idle';
        });
    }

    startParade(activeIDs: string[]) {
        const paradeHeroes: HeroController[] = [];

        this.clearTracks();

        activeIDs.forEach((id) => {
            const hero = this.heroes.get(id);
            if (!hero) {
                console.warn(`Racer with ID ${id} does not exist.`);
                return;
            }
            paradeHeroes.push(hero);
        });

        if (isMobile.any) {
            this.parade.startMobile(this.tracksContainers, paradeHeroes);
        } else {
            this.parade.start(this.tracksContainers, paradeHeroes);
        }
    }

    stopParade() {
        this.parade.stop();
        this.setAllIdle();
        this.resetPositions();
    }

    positionRacersOnTracks(activeIDs: string[]) {
        this.stopParade();
        this.clearTracks();

        activeIDs.forEach((id, i) => {
            const hero = this.heroes.get(id);
            const trackContainer = this.tracksContainers[i];

            if (!hero) {
                console.warn(`Racer with ID ${id} does not exist.`);
                return;
            }

            if (!trackContainer) {
                console.warn(`Track container ${i} does not exist.`);
                return;
            }

            trackContainer.addChild(hero);
        });
    }

    selectedHero(heroID?: string) {
        if (!heroID) return;

        const hero = this.heroes.get(heroID);
        if (hero) {
            hero.color = 'green';
            hero.playSelectedAnimation();
        }
    }

    unselectAll() {
        this.heroes.forEach((hero) => hero.color = 'white');
    }

    setAllRun() {
        this.heroes.forEach((hero) => {
            hero.state = 'run';
        });
    }

    handlePositionChanges(activeHeroes: HeroData[]) {
        const now = performance.now();

        activeHeroes.forEach((heroData) => {
            const previousPosition = this.lastPositions.get(heroData.name);
            this.lastPositions.set(heroData.name, heroData.position);

            if (previousPosition === undefined || previousPosition === heroData.position) {
                return;
            }

            if (heroData.finished) return;

            const lastAt = this.lastAnimationAt.get(heroData.name) ?? 0;
            if (now - lastAt < POSITION_ANIMATION_COOLDOWN_MS) return;

            const hero = this.heroes.get(heroData.name);
            if (!hero) return;

            if (heroData.position < previousPosition) {
                hero.speedUp();
            } else {
                hero.slowDown();
            }

            this.lastAnimationAt.set(heroData.name, now);
        });
    }

    resetPositionTracking() {
        this.lastPositions.clear();
        this.lastAnimationAt.clear();
    }

    showWinner(heroID: string) {
        const hero = this.heroes.get(heroID);

        if (hero) {
            hero.state = 'win';
        }
    }

    updatePositions(positions: Record<string, number>) {
        this.heroes.forEach((hero, id) => {
            hero.moveTo(positions[id] || 0);
        });
    }
}
